import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { shallow } from 'zustand/shallow';

import { type TransferSearchTransfersData, type UnifiedTransfer, transferSearchTransfersOptions } from '@column/api';
import { Button, Icon } from '@column/column-ui-kit';
import { DataTable } from '@column/ui';

import { TransferFiltersModal } from '../Transfers/_components/TransferFiltersModal';
import {
  PageTransferExportModalContent,
  ResetFilter,
  TransferLocationState,
  TransferSearch,
} from '../Transfers/Overview';
import { normalizeTransferTypes } from '~/app/pages/Transfers/_utils/filterUtils';
import { ROUTE } from '~/app/routes';
import {
  Loading,
  PopoverFilterAmount,
  PopoverFilterTransferDate,
  PopoverFilterTransferStatus,
  PopoverFilterTransferType,
  PopoverFilterWrapper,
} from '~/components';
import { useDialog } from '~/components/Dialog/Provider';
import { Search } from '~/components/Search';
import { Empty as TableEmpty, Pagination as TablePagination, tableDefaults } from '~/components/Table';
import { useCheckTransferId } from '~/hooks/useTransfers';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { formatTransferCount, toTransferApiQuery, transferTableColumns, useTransfersFilters } from '~/lib/transfers';
import { getTransferURL } from '~/lib/transfers/utils';
import { TransferEntryType, TransferRepository, transfers } from '~/repositories';
import { TransferTypeOptions } from '~/repositories/Transfer/TransferRepository';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { useScrollStore } from '~/stores/Scroll';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { downloadBlob, getDateFormat } from '~/util';

import { BankAccountSectionProps } from './Section';

const PAGE_TABS_STICKY_HEIGHT = 40;

const MobileFiltersWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const Toolbar = styled.div<{ $isScrolled: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px var(--page-margin);
  position: sticky;
  top: ${PAGE_TABS_STICKY_HEIGHT}px;
  z-index: 9;
  background: ${({ theme }) => theme.background};
  border-bottom: 1px solid;
  border-bottom-color: transparent;
  transition: border-bottom-color 0.1s;

  ${({ $isScrolled }) =>
    $isScrolled &&
    css`
      border-bottom-color: var(--color-border);
    `}
`;

const FilterActions = styled.div<{ $isHidden?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: ${({ $isHidden }) => ($isHidden ? 0 : '0 8px')};
  transition: opacity 0.2s;
  width: ${({ $isHidden }) => ($isHidden ? 0 : 'auto')};
  opacity: ${({ $isHidden }) => ($isHidden ? 0 : 1)};
  pointer-events: ${({ $isHidden }) => ($isHidden ? 'none' : 'auto')};
`;

export const PageBankAccountsTransfers: React.FC = () => {
  const { openAlert, closeAlert, getAlertData, setAlertData } = useDialog();
  const { bankAccount } = useOutletContext<BankAccountSectionProps>();
  const navigate = useNavigate();
  const location = useLocation();
  const { addDangerNotification } = useNotificationStore();
  const pageFrameScroll = useScrollStore((s) => s.pageFrameScroll);
  const { currentPlatform } = useSessionStore();
  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const pageFrameScrollTop = pageFrameScroll ?? 0;
  const toolbarOffsetTop = toolbarRef.current?.offsetTop;
  const isToolbarAtTop =
    pageFrameScrollTop > 0 &&
    typeof toolbarOffsetTop === 'number' &&
    pageFrameScrollTop >= toolbarOffsetTop - PAGE_TABS_STICKY_HEIGHT - 1;

  const locationState = location.state as TransferLocationState;

  const mountedRef = useRef<boolean>(false);

  const isTransferSearchEnabled = useFeatureFlag(FeatureFlag.EnableTransferSearch);

  const [filterByIdTooltipContent, setFilterByIdTooltipContent] = useState<string>('');
  const [filterByIdValue, setFilterByIdValue] = useState<string>('');

  const { pageSize, timeFormat, update: updateTableConfig } = useTableConfig('transfers');

  const {
    queryInput,
    debouncedQueryInput,
    handleQueryInputChange: hookHandleQueryInputChange,
    cancelApplyQuery,
    amountFilterData,
    setAmountFilterData,
    isAmountFilterActive,
    transferTypeFilterData,
    setTransferTypeFilterData,
    isTransferTypeFilterActive,
    transferStatusFilterData,
    setTransferStatusFilterData,
    transferDateFilterData,
    setTransferDateFilterData,
    isDateFilterActive,
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    resetPagination,
    setPageSize,
    filters,
    isFilterActive: isHookFilterActive,
    resetAllFilters,
    queryParams,
    setUrlQueryParams,
    setQueryInput,
    setDebouncedQueryInput,
  } = useTransfersFilters({
    bankAccountId: bankAccount.id,
    defaultPageSize: pageSize,
    isTransferSearchEnabled,
  });

  const handleTimeFormatChange = useCallback(
    (value: TableTimeFormat) => {
      updateTableConfig({ timeFormat: value }).catch(() => {
        addDangerNotification({ content: 'Failed to save time format preference' });
      });
    },
    [updateTableConfig, addDangerNotification]
  );

  const columns = useMemo(
    () =>
      transferTableColumns({
        bankAccountId: bankAccount.id,
        timeFormat,
        onTimeFormatChange: handleTimeFormatChange,
      }),
    [bankAccount.id, timeFormat, handleTimeFormatChange]
  );

  const [isSmallViewport, setIsSmallViewport] = useState<boolean>(false);

  useEffect(() => {
    if (!queryParams) {
      return undefined;
    }

    const { counterpartyId, accountNumberId } = queryParams;

    setFilterByIdValue(counterpartyId || accountNumberId || '');
  }, []);

  const handleFilterByIdValueChange = useCallback((value: string) => {
    setFilterByIdValue(value);
    setFilterByIdTooltipContent('');
  }, []);

  const handleQueryInputChange = useCallback(
    (value: string) => {
      setFilterByIdTooltipContent('');
      hookHandleQueryInputChange(value);
    },
    [hookHandleQueryInputChange]
  );

  const isFilterActive = useMemo(
    () => Boolean(isHookFilterActive || (!isTransferSearchEnabled && filterByIdValue)),
    [isHookFilterActive, isTransferSearchEnabled, filterByIdValue]
  );
  const isEmptyStateFilterActive = useMemo(() => {
    const hasAppliedFilters = Object.keys(filters).some((key) => key !== 'bankAccountId');
    return Boolean(hasAppliedFilters || (!isTransferSearchEnabled && filterByIdValue));
  }, [filters, isTransferSearchEnabled, filterByIdValue]);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window === 'undefined') {
        return;
      }
      setIsSmallViewport(window.innerWidth <= 1350);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const checkTransferId = useCheckTransferId({
    onSuccess: (id) => {
      const url = transfers.find((transfer) => transfer.idString && id.startsWith(transfer.idString))?.id;

      navigate(`${ROUTE.TRANSFERS}/edit/${url}/${id}`);
    },
    onError: (message) => {
      setFilterByIdTooltipContent(message);
    },
  });

  const apiQuery = useMemo<NonNullable<TransferSearchTransfersData['query']>>(
    () => ({
      limit: pageSize,
      ...(cursor ? { starting_after: cursor } : {}),
      ...toTransferApiQuery(filters),
    }),
    [pageSize, cursor, filters]
  );

  const {
    data: response,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    ...transferSearchTransfersOptions({ query: apiQuery }),
    enabled: Boolean(currentPlatform?.id),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });

  const transferList = useMemo<UnifiedTransfer[]>(() => response?.transfers ?? [], [response]);
  const hasMore = response?.has_more ?? false;
  const totalResultsCount = response?.total_results_count ?? 0;

  const singleResult = useMemo<UnifiedTransfer | null>(() => {
    if (isFetching) return null;
    return transferList.length === 1 && !hasMore ? transferList[0] : null;
  }, [transferList, hasMore, isFetching]);

  const isInitialFilterRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialFilterRenderRef.current) {
      isInitialFilterRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [
    debouncedQueryInput,
    queryParams?.counterpartyId,
    queryParams?.accountNumberId,
    queryParams?.transferBatchId,
    transferTypeFilterData,
    transferStatusFilterData,
    amountFilterData,
    transferDateFilterData,
    resetPagination,
  ]);

  const showEnterHint = useMemo(
    () =>
      isTransferSearchEnabled && queryInput.length > 0 && debouncedQueryInput === queryInput && singleResult !== null,
    [isTransferSearchEnabled, debouncedQueryInput, queryInput, singleResult]
  );

  const handleQuerySubmit = useCallback(
    (value: string) => {
      if (showEnterHint && singleResult) {
        navigate(getTransferURL(singleResult.id, singleResult.type as TransferEntryType));
        return;
      }

      cancelApplyQuery();
      setQueryInput(value);
      setDebouncedQueryInput(value);
    },
    [cancelApplyQuery, navigate, setQueryInput, setDebouncedQueryInput, showEnterHint, singleResult]
  );

  const handleFilterById = useCallback(
    (value: string) => {
      if (value.length > 0 && transfers.some((transfer) => transfer.idString && value.startsWith(transfer.idString))) {
        checkTransferId(value);

        return;
      }

      if (value.startsWith('cpty_')) {
        setUrlQueryParams((prev) => ({
          ...(prev ?? {}),
          counterpartyId: value,
          accountNumberId: undefined,
        }));

        return;
      }

      if (value.startsWith('acno_')) {
        setUrlQueryParams((prev) => ({
          ...(prev ?? {}),
          accountNumberId: value,
          counterpartyId: undefined,
        }));

        return;
      }

      if (value && value.length > 0) {
        setFilterByIdTooltipContent('Not a supported ID. Enter a Bank Account ID, Counterparty ID, or Transfer ID.');
      } else {
        setUrlQueryParams(() => undefined);
        setFilterByIdTooltipContent('');
      }
    },
    [checkTransferId, setUrlQueryParams]
  );

  useLayoutEffect(() => {
    if (locationState?.trigger === 'delayedFetch') {
      window.history.replaceState({}, '');
      setTimeout(() => {
        refetch();
        setTimeout(() => {
          refetch();
        }, 1000);
      }, 500);
    }
  }, [locationState?.trigger, refetch]);

  const handleFilterReset = useCallback(() => {
    resetAllFilters();
    setFilterByIdValue('');
    setFilterByIdTooltipContent('');
  }, [resetAllFilters]);

  const handlePageNext = useCallback(() => {
    if (transferList.length > 0) {
      onNext({ cursor: transferList[transferList.length - 1].id });
    }
  }, [transferList, onNext]);

  const handlePageSizeChange = useCallback(
    (size: number) => {
      setPageSize(size);
      updateTableConfig({ limit: size }).catch(() => {
        addDangerNotification({ content: 'Failed to save page size preference' });
      });
    },
    [setPageSize, updateTableConfig, addDangerNotification]
  );

  const [exportAnimate, setExportAnimate] = useState<boolean>(false);
  const handleExport = () => {
    if (getAlertData('export-transfers')?.isLoading) {
      return;
    }

    setAlertData('export-transfers', { isLoading: true });

    setExportAnimate(true);

    TransferRepository.export(filters)
      .then((csv) => {
        downloadBlob(
          csv,
          `${
            String(getAlertData('export-transfers')?.customFileName).length > 0
              ? getAlertData('export-transfers')?.customFileName
              : `transfers-${getDateFormat(new Date())}`
          }.csv`
        );
      })
      .then(() => {
        closeAlert();
      })
      .catch((e) =>
        addDangerNotification({
          content: e.message,
          display: 'popup',
        })
      )
      .finally(() => {
        setAlertData('export-transfers', { isLoading: false });
      });
  };

  const handleExportClick = () => {
    const count = formatTransferCount(totalResultsCount);
    let text = `Do you want to export & download the .csv file for ${count} transfers?`;

    if (!isFilterActive) {
      text = `Are you sure you want to export & download the .csv file for all transfers (${count}) without any filters applied?`;
    }

    if (totalResultsCount > 1000) {
      text = `Are you sure to export & download the .csv file for ${count} transfers? This may take a while.`;
    }

    openAlert({
      headline: 'Export',
      content: <PageTransferExportModalContent text={text} />,
      confirmButton: {
        label: 'Download',
        variant: 'primary',
      },
      onSuccess: handleExport,
      type: 'primary',
      icon: 'AnimationDownload',
    });
  };

  useEffect(() => {
    setTimeout(() => {
      mountedRef.current = true;
    }, 2000);
  }, []);

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => [state.isSandbox, state.currentUser?.defaultPlatformId],
        () => {
          if (!mountedRef.current) {
            return;
          }

          handleFilterReset();
        },
        {
          equalityFn: shallow,
        }
      ),
    []
  );

  return (
    <>
      <TransferFiltersModal
        isOpen={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        hideAccount
        initialAmount={amountFilterData}
        initialType={{
          isIncoming: transferTypeFilterData.isIncoming,
          type: transferTypeFilterData.type as unknown as string[],
        }}
        initialStatus={transferStatusFilterData}
        initialDate={{
          createdAtGte: transferDateFilterData?.createdAtGte,
          createdAtLt: transferDateFilterData?.createdAtLt,
        }}
        isTransferSearchEnabled={isTransferSearchEnabled}
        initialQueryValue={isTransferSearchEnabled ? debouncedQueryInput : filterByIdValue}
        onApply={(values) => {
          setAmountFilterData({ amount: values.amount, amountGte: values.amountGte, amountLte: values.amountLte });
          setTransferTypeFilterData({
            isIncoming: values.isIncoming,
            type: normalizeTransferTypes(
              values.type as unknown[] | undefined
            ) as unknown as (typeof TransferTypeOptions)[],
          });
          setTransferStatusFilterData(values.status);
          setTransferDateFilterData({ createdAtGte: values.createdAtGte, createdAtLt: values.createdAtLt });
          const query = values.query ?? '';
          if (isTransferSearchEnabled) {
            cancelApplyQuery();
            setQueryInput(query);
            setDebouncedQueryInput(query);
          } else if (query.length > 0) {
            handleFilterById(query);
          }
        }}
      />

      <Toolbar ref={toolbarRef} $isScrolled={isToolbarAtTop}>
        {isSmallViewport ? (
          <MobileFiltersWrapper>
            <Button size="small" variant="secondary" onClick={() => setIsFiltersOpen(true)}>
              <Icon.Filter />
              Filters
            </Button>

            <FilterActions $isHidden={!isFilterActive}>
              <ResetFilter size="small" variant="subtle-primary" icon={<Icon.Reset />} onClick={handleFilterReset}>
                Reset
              </ResetFilter>
            </FilterActions>
          </MobileFiltersWrapper>
        ) : (
          <>
            <PopoverFilterWrapper
              label="Amount"
              onSubmit={(data) => setAmountFilterData(data)}
              isActive={isAmountFilterActive}
            >
              <PopoverFilterAmount data={amountFilterData ?? {}} />
            </PopoverFilterWrapper>
            <PopoverFilterWrapper
              label="Type"
              onSubmit={(data) => setTransferTypeFilterData(data)}
              isActive={isTransferTypeFilterActive}
            >
              <PopoverFilterTransferType data={transferTypeFilterData ?? {}} />
            </PopoverFilterWrapper>
            <PopoverFilterWrapper
              label="Status"
              onSubmit={(data) => setTransferStatusFilterData(data)}
              isActive={transferStatusFilterData && transferStatusFilterData.length > 0}
            >
              <PopoverFilterTransferStatus data={transferStatusFilterData ?? []} />
            </PopoverFilterWrapper>
            <PopoverFilterWrapper
              label="Date"
              onSubmit={(data) => setTransferDateFilterData(data)}
              isActive={isDateFilterActive}
            >
              <PopoverFilterTransferDate data={transferDateFilterData ?? {}} />
            </PopoverFilterWrapper>

            {isTransferSearchEnabled ? (
              <TransferSearch
                value={queryInput}
                onValueChange={handleQueryInputChange}
                onFilterSubmit={handleQuerySubmit}
                placeholder="Search transfers..."
                tooltipContent={filterByIdTooltipContent}
                showEnterHint={showEnterHint}
                width={250}
              />
            ) : (
              <Search
                value={filterByIdValue}
                onFilterSubmit={handleFilterById}
                placeholder="Filter by ID"
                tooltipContent={filterByIdTooltipContent}
                onValueChange={handleFilterByIdValueChange}
              />
            )}

            {isFilterActive && (
              <ResetFilter
                size="small"
                variant="subtle-primary"
                icon={<Icon.Reset />}
                iconRight
                onClick={handleFilterReset}
              >
                Reset filters
              </ResetFilter>
            )}
          </>
        )}

        <div className="ml-auto">
          <Button
            icon={<Icon.AnimationDownload running={exportAnimate} onDone={() => setExportAnimate(false)} />}
            size="small"
            variant="secondary"
            onClick={handleExportClick}
          >
            Export
          </Button>
        </div>
      </Toolbar>

      <Loading isLoading={isLoading}>
        <Inner pt={0} px="var(--page-margin)">
          <DataTable<UnifiedTransfer>
            {...tableDefaults}
            columns={columns}
            data={transferList}
            rowKey={(row) => row.id}
            isLoading={isFetching}
            emptyContent={
              <TableEmpty
                icon={<Icon.AnimationArrows />}
                title="No transfers found"
                description={
                  isEmptyStateFilterActive
                    ? 'No transfers were found matching the specified filters'
                    : 'No transfers were found for this account'
                }
              />
            }
            onRowClick={(row) => navigate(getTransferURL(row.id, row.type as TransferEntryType))}
          />

          <TablePagination
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            current={pageIndex + 1}
            hasPrevious={hasPrevious}
            hasNext={hasMore}
            onPrevious={onPrevious}
            onNext={handlePageNext}
          />
        </Inner>
      </Loading>
    </>
  );
};
